/**
 * Review & Verify View.
 *
 * Shows per-topic narrative summaries with confidence rings.
 * Users can edit summaries inline or go back to chat for substantial changes.
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Pencil, X, ArrowLeft, Loader2, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Markdown } from '@/components/common/Markdown';
import { ConfidenceRing } from '@/components/interview/ConfidenceRing';
import { HobbesAvatar } from '@/components/interview/HobbesAvatar';
import type { TopicReviewSummary } from '@/lib/api/publicInterview';

// ========================================
// ANIMATION VARIANTS
// ========================================

const containerVariants = {
  initial: {},
  animate: { transition: { staggerChildren: 0.15, delayChildren: 0.1 } },
};

const cardVariants = {
  initial: { opacity: 0, y: 12 },
  animate: {
    opacity: 1, y: 0,
    transition: { duration: 0.4, ease: [0.16, 1, 0.3, 1] },
  },
  exit: { opacity: 0, y: -8 },
};

const fadeSlide = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -12 },
};

// ========================================
// PROPS
// ========================================

interface ReviewViewProps {
  summaries: TopicReviewSummary[];
  topicConfidence: Record<string, number>;
  onUpdateTopic: (topicId: string, editedSummary: string) => Promise<void>;
  onConfirm: () => void;
  onBackToChat: () => void;
  isGenerating: boolean;
  isUpdating: boolean;
  isConfirming: boolean;
  interviewerName?: string | null;
  logoUrl?: string | null;
}

// ========================================
// TOPIC REVIEW CARD
// ========================================

const TopicReviewCard: React.FC<{
  summary: TopicReviewSummary;
  confidence: number;
  onUpdate: (editedSummary: string) => Promise<void>;
  isUpdating: boolean;
  onConfirmTopic: () => void;
}> = ({ summary, confidence, onUpdate, isUpdating, onConfirmTopic }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [isConfirmed, setIsConfirmed] = useState(summary.confirmed);

  const displayText = summary.edited_summary || summary.summary;

  const handleStartEdit = () => {
    setEditValue(displayText);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditValue('');
  };

  const handleSaveEdit = async () => {
    if (!editValue.trim() || editValue.trim() === displayText) {
      setIsEditing(false);
      return;
    }
    await onUpdate(editValue.trim());
    setIsEditing(false);
  };

  const handleConfirmTopic = () => {
    setIsConfirmed(true);
    onConfirmTopic();
  };

  return (
    <motion.div
      variants={cardVariants}
      className={`rounded-xl border p-5 transition-colors ${
        isConfirmed
          ? 'border-border bg-muted/30'
          : 'border-border bg-card'
      }`}
    >
      {/* Header: topic name + confidence */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {isConfirmed && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            >
              <Check className="h-4 w-4 text-muted-foreground" />
            </motion.div>
          )}
          <h3 className="text-sm font-semibold text-foreground">{summary.topic_name}</h3>
        </div>
        {confidence > 0 && <ConfidenceRing value={confidence} size={24} />}
      </div>

      {/* Content: view or edit mode */}
      <AnimatePresence mode="wait">
        {isEditing ? (
          <motion.div key="edit" {...fadeSlide} transition={{ duration: 0.15 }}>
            <textarea
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm text-foreground outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 resize-none"
              rows={4}
              autoFocus
            />
            <div className="flex justify-end gap-2 mt-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCancelEdit}
                className="text-xs h-8"
              >
                <X className="h-3.5 w-3.5 mr-1" />
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSaveEdit}
                disabled={isUpdating || !editValue.trim()}
                className="text-xs h-8"
              >
                {isUpdating ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                ) : (
                  <Check className="h-3.5 w-3.5 mr-1" />
                )}
                Save
              </Button>
            </div>
          </motion.div>
        ) : (
          <motion.div key="view" {...fadeSlide} transition={{ duration: 0.15 }}>
            <div className="text-sm text-muted-foreground leading-relaxed">
              <Markdown content={displayText} className="prose-sm" />
            </div>
            {!isConfirmed && (
              <div className="flex gap-2 mt-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleStartEdit}
                  className="text-xs h-8"
                >
                  <Pencil className="h-3.5 w-3.5 mr-1" />
                  Edit
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleConfirmTopic}
                  className="text-xs h-8"
                >
                  <Check className="h-3.5 w-3.5 mr-1" />
                  Looks Good
                </Button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// ========================================
// SKELETON CARDS
// ========================================

const ReviewSkeleton: React.FC = () => (
  <div className="space-y-4">
    {[1, 2, 3].map((i) => (
      <div key={i} className="rounded-xl border border-border bg-card p-5 animate-pulse">
        <div className="flex items-center justify-between mb-3">
          <div className="h-4 w-32 bg-muted rounded" />
          <div className="h-6 w-12 bg-muted rounded" />
        </div>
        <div className="space-y-2">
          <div className="h-3 w-full bg-muted rounded" />
          <div className="h-3 w-4/5 bg-muted rounded" />
          <div className="h-3 w-3/5 bg-muted rounded" />
        </div>
      </div>
    ))}
  </div>
);

// ========================================
// MAIN REVIEW VIEW
// ========================================

export const ReviewView: React.FC<ReviewViewProps> = ({
  summaries,
  topicConfidence,
  onUpdateTopic,
  onConfirm,
  onBackToChat,
  isGenerating,
  isUpdating,
  isConfirming,
  interviewerName,
  logoUrl,
}) => {
  const [confirmedTopics, setConfirmedTopics] = useState<Set<string>>(new Set());

  const handleConfirmTopic = (topicId: string) => {
    setConfirmedTopics((prev) => new Set(prev).add(topicId));
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className="h-full overflow-y-auto"
    >
      <div className="max-w-[720px] mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-start gap-3 mb-8">
          <HobbesAvatar size="md" />
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              Review Your Responses
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {interviewerName
                ? `Edit anything ${interviewerName} shouldn't retain before you submit.`
                : "Edit anything that doesn't look right before you submit."}
            </p>
          </div>
        </div>

        {/* Topic cards */}
        {isGenerating ? (
          <>
            <p className="text-sm text-muted-foreground mb-4 animate-pulse">
              Reviewing your responses...
            </p>
            <ReviewSkeleton />
          </>
        ) : summaries.length === 0 ? (
          <div className="rounded-xl border border-dashed p-6 text-sm text-muted-foreground">
            There isn’t anything new to review yet. Go back to chat and answer the follow-up question first.
          </div>
        ) : (
          <motion.div
            variants={containerVariants}
            initial="initial"
            animate="animate"
            className="space-y-4"
          >
            {summaries.map((summary) => (
              <TopicReviewCard
                key={summary.topic_id}
                summary={summary}
                confidence={(topicConfidence[summary.topic_id] ?? summary.confidence_level) ?? 0}
                onUpdate={(edited) => onUpdateTopic(summary.topic_id, edited)}
                isUpdating={isUpdating}
                onConfirmTopic={() => handleConfirmTopic(summary.topic_id)}
              />
            ))}
          </motion.div>
        )}

        {/* Footer actions */}
        {!isGenerating && summaries.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: summaries.length * 0.15 + 0.2 }}
            className="flex items-center justify-between mt-8 pt-6 border-t border-border"
          >
            <Button
              variant="ghost"
              onClick={onBackToChat}
              disabled={isConfirming}
              className="text-sm"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Chat
            </Button>
            <Button
              onClick={onConfirm}
              disabled={isConfirming}
              className="text-sm"
            >
              {isConfirming ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Finalizing...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Confirm & Finish
                </>
              )}
            </Button>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};
